import { useEffect, useRef } from "react";
import { IconPlus } from "./Icons";

export interface Waiting {
  key: string;
  label: string;
}

/**
 * The "+" at the top right of a form section: puts a field that is in the
 * text but in no section yet into this one.
 *
 * It offers only those fields and has no way to make a new one. A field
 * exists because its {{placeholder}} is in the document; a field created
 * here would have nowhere in the text to appear. The red dot says some are
 * waiting, so a placeholder typed into a clause is not silently left out
 * of the form.
 *
 * Controlled (`open`), so the gap list can open it for a waiting field.
 */
export function FieldPicker({
  section,
  waiting,
  open,
  onToggle,
  onPick,
  disabled,
}: {
  section: string;
  waiting: Waiting[];
  open: boolean;
  onToggle: (open: boolean) => void;
  onPick: (key: string) => void;
  disabled?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) onToggle(false);
    }
    function esc(e: KeyboardEvent) {
      if (e.key === "Escape") onToggle(false);
    }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open, onToggle]);

  return (
    <div className="picker" ref={box}>
      <button
        type="button"
        className="picker-btn"
        aria-label={`Adaugă un câmp în ${section}`}
        aria-expanded={open}
        title="Adaugă un câmp din text"
        disabled={disabled}
        onClick={() => onToggle(!open)}
      >
        <IconPlus size={16} />
        {waiting.length > 0 && (
          <span className="picker-dot" aria-label={`${waiting.length} câmpuri fără categorie`} />
        )}
      </button>
      {open && (
        <div className="picker-menu" role="menu">
          {waiting.length === 0 ? (
            <p className="picker-empty">
              Toate câmpurile din text au deja o categorie. Un câmp nou apare
              aici după ce scrieți {"{{nume}}"} într-o clauză.
            </p>
          ) : (
            <>
              <p className="picker-title">Câmpuri din text fără categorie</p>
              {waiting.map((w) => (
                <button
                  type="button"
                  role="menuitem"
                  key={w.key}
                  className="picker-item"
                  onClick={() => {
                    onPick(w.key);
                    onToggle(false);
                  }}
                >
                  <span>{w.label}</span>
                  <code>{`{{${w.key}}}`}</code>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
